import { useMemo, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import type { ArcadeSpotSpec, ArcadeTvSpec } from "../scenario/types";
import { arcadeScenarioId, useArcadeProgress } from "../devices/arcade/results";
import { spotStates } from "../devices/arcade/hub";

/** Screen-space pin (percent of viewport) for one arcade spot. */
export type ArcadeSpotPin = { xPct: number; yPct: number; visible: boolean; distance: number };
export type ArcadeSpotState = "active" | "done" | "hidden";

const ARROW_COLOR = new THREE.Color("#2fe6ff");
const ARROW_GLOW = new THREE.Color("#ff3df2");
const DONE_COLOR = new THREE.Color("#48f07a");

/**
 * Hovering neon arrows above the playable cabinets in a walkable arcade, plus
 * the briefing TV on the wall. Arrows bob, spin and blink while a cabinet can
 * be played; a finished cabinet shows a small green check instead. The DOM
 * click labels are positioned from the projected pins reported here (same
 * technique as CounterHotspot).
 */
export function ArcadeMarkers({
  spots,
  hubActive,
  tv,
  onPins,
}: {
  spots: ArcadeSpotSpec[];
  hubActive: boolean;
  tv?: ArcadeTvSpec;
  onPins?: (pins: Record<string, ArcadeSpotPin>) => void;
}) {
  const { camera } = useThree();
  const progress = useArcadeProgress(arcadeScenarioId());
  const states: Record<string, ArcadeSpotState> = spotStates(spots, progress, hubActive);
  const groups = useRef<Record<string, THREE.Group | null>>({});
  const mats = useRef<Record<string, THREE.MeshBasicMaterial | null>>({});
  const last = useRef<Record<string, ArcadeSpotPin>>({});
  const onPinsRef = useRef(onPins);
  onPinsRef.current = onPins;
  const tmp = useMemo(() => ({ dir: new THREE.Vector3(), to: new THREE.Vector3(), p: new THREE.Vector3() }), []);

  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    camera.getWorldDirection(tmp.dir);
    let changed = false;
    const next: Record<string, ArcadeSpotPin> = {};
    spots.forEach((s, i) => {
      const st = states[s.id] ?? "hidden";
      const g = groups.current[s.id];
      if (g) {
        g.visible = st !== "hidden";
        const bob = st === "active" ? Math.sin(t * 2.4 + i) * 0.07 : 0;
        g.position.set(s.arrow[0], s.arrow[1] + bob, s.arrow[2]);
        g.rotation.y = st === "active" ? t * 1.6 + i : 0;
      }
      const m = mats.current[s.id];
      if (m && st === "active") {
        const k = 0.5 + 0.5 * Math.sin(t * 6 + i);
        m.color.copy(ARROW_COLOR).lerp(ARROW_GLOW, k * 0.55);
        m.opacity = 0.7 + 0.3 * (Math.sin(t * 9 + i) > -0.3 ? 1 : 0.2);
      }
      const anchor = s.pin ?? s.arrow;
      tmp.p.set(anchor[0], anchor[1], anchor[2]);
      tmp.to.copy(tmp.p).sub(camera.position);
      const distance = tmp.to.length();
      const inFront = tmp.to.normalize().dot(tmp.dir) > 0.1;
      tmp.p.project(camera);
      const xPct = (tmp.p.x * 0.5 + 0.5) * 100;
      const yPct = (-tmp.p.y * 0.5 + 0.5) * 100;
      const onScreen = xPct > -5 && xPct < 105 && yPct > -5 && yPct < 105;
      const pin: ArcadeSpotPin = {
        xPct,
        yPct,
        visible: st !== "hidden" && inFront && onScreen && distance < (s.maxDistance ?? 7),
        distance,
      };
      next[s.id] = pin;
      const p = last.current[s.id];
      if (
        !p ||
        p.visible !== pin.visible ||
        Math.abs(p.xPct - pin.xPct) > 0.3 ||
        Math.abs(p.yPct - pin.yPct) > 0.3 ||
        Math.abs(p.distance - pin.distance) > 0.05
      ) {
        changed = true;
      }
    });
    if (changed) {
      last.current = next;
      onPinsRef.current?.(next);
    }
  });

  return (
    <group>
      {spots.map((s) => {
        const st = states[s.id] ?? "hidden";
        return (
          <group key={s.id} ref={(g) => (groups.current[s.id] = g)}>
            {st === "done" ? (
              <group rotation={[0, s.facingDeg ? (s.facingDeg * Math.PI) / 180 : 0, 0]}>
                <mesh position={[-0.05, -0.02, 0]} rotation={[0, 0, Math.PI / 4]}>
                  <boxGeometry args={[0.04, 0.14, 0.04]} />
                  <meshBasicMaterial color={DONE_COLOR} toneMapped={false} />
                </mesh>
                <mesh position={[0.06, 0.04, 0]} rotation={[0, 0, -Math.PI / 5]}>
                  <boxGeometry args={[0.04, 0.26, 0.04]} />
                  <meshBasicMaterial color={DONE_COLOR} toneMapped={false} />
                </mesh>
              </group>
            ) : (
              <>
                <mesh position={[0, 0.16, 0]}>
                  <cylinderGeometry args={[0.045, 0.045, 0.22, 12]} />
                  <meshBasicMaterial
                    ref={(m) => (mats.current[s.id] = m)}
                    color={ARROW_COLOR}
                    transparent
                    toneMapped={false}
                  />
                </mesh>
                <mesh position={[0, -0.02, 0]} rotation={[Math.PI, 0, 0]}>
                  <coneGeometry args={[0.13, 0.18, 4]} />
                  <meshBasicMaterial color={ARROW_COLOR} toneMapped={false} />
                </mesh>
                <mesh position={[0, -0.02, 0]} rotation={[Math.PI, 0, 0]} scale={1.35}>
                  <coneGeometry args={[0.13, 0.18, 4]} />
                  <meshBasicMaterial color={ARROW_GLOW} transparent opacity={0.22} toneMapped={false} depthWrite={false} />
                </mesh>
              </>
            )}
          </group>
        );
      })}
      {tv && <ArcadeTvMesh tv={tv} />}
    </group>
  );
}

/** Wall TV used for the briefing and the final verdict. The picture itself is DOM, glued via screenQuad. */
function ArcadeTvMesh({ tv }: { tv: ArcadeTvSpec }) {
  const rot = ((tv.rotationDeg ?? 0) * Math.PI) / 180;
  const bez = tv.bezel ?? 0.06;
  return (
    <group position={tv.center as [number, number, number]} rotation={[0, rot, 0]}>
      <mesh position={[0, 0, -0.04]}>
        <boxGeometry args={[tv.width + bez * 2, tv.height + bez * 2, 0.06]} />
        <meshStandardMaterial color="#0c0c10" roughness={0.45} metalness={0.3} />
      </mesh>
      <mesh position={[0, 0, -0.008]}>
        <planeGeometry args={[tv.width, tv.height]} />
        <meshBasicMaterial color="#071526" toneMapped={false} />
      </mesh>
      <mesh position={[0, -tv.height / 2 - bez + 0.012, 0.0]}>
        <boxGeometry args={[tv.width * 0.5, 0.008, 0.01]} />
        <meshBasicMaterial color="#2fe6ff" toneMapped={false} />
      </mesh>
    </group>
  );
}
